import { z } from "zod";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, rateSchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { posLineKindSchema, posTenderTypeSchema } from "./enums.js";
/**
 * `PosSale` / `PosSaleVoid` olay yükleri — `docs/domain/pos-local-api.md` §3/§4 gövdesinin ajan tarafından
 * TAMAMLANMIŞ hâli (ajan fişi ve toplamları ekler). Ajan protokolü §3.1'de kayıtlıdır.
 *
 * **Şema bilinçli olarak GEVŞEKTİR.** Prompt 14 dersi: kalıcı ret (`rejected`) ajanın `ackedSeq`'ini ilerletir
 * ve olay bir daha GÖNDERİLMEZ — bir satışı doğrulama hatasıyla reddetmek doğrudan SATIŞ KAYBIDIR. Bu yüzden:
 *  · yalnızca `agentSaleId` ve en az bir satır ZORUNLUDUR;
 *  · bilinmeyen ürün / KDV kodu / cari / terminal / personel satırı DÜŞÜRMEZ — sunucu kaydı yazar ve
 *    `pos_sales.warnings` alanına uyarı koyar (`POS_SALE_WARNINGS`);
 *  · tutar alanları dizge ya da tam sayı olarak kabul edilir (eski / farklı istemciler için);
 *  · `totals` verilmezse sunucu satırlardan hesaplar.
 */
/** Kuruş: dizge ya da tam sayı kabul edilir; dizgeye normalleştirilir. */
const looseMoney = z
    .union([moneySchema, z.number().int()])
    .transform((value) => (typeof value === "number" ? value.toString() : value));
const looseNonNegativeMoney = z
    .union([nonNegativeMoneySchema, z.number().int().nonnegative()])
    .transform((value) => (typeof value === "number" ? value.toString() : value));
/** Miktar: ≤ 3 ondalık dizge ya da sayı. */
const looseQuantity = z
    .union([quantitySchema, z.number()])
    .transform((value) => (typeof value === "number" ? value.toFixed(3) : value));
const looseUnitPrice = z
    .union([unitPriceSchema, z.number()])
    .transform((value) => (typeof value === "number" ? value.toFixed(4) : value));
/** Bilinmeyen tür ITEM sayılır: satır düşmez, satış kaydedilir. */
const lineKind = posLineKindSchema.catch("ITEM");
/**
 * Eski ajan / POS sürümlerinin puan ödemesi adları (ADR 0017 §28): Prompt 15 POS'u `POINTS`, Go ajanı `LOYALTY`
 * gönderiyordu. İkisi de `LOYALTY_POINTS` sayılır.
 */
export const LEGACY_POS_TENDER_ALIASES = {
    POINTS: "LOYALTY_POINTS",
    LOYALTY: "LOYALTY_POINTS",
};
/**
 * Ham tahsilat türünü normalleştirir. Bilinmeyen tür CASH SAYILMAZ: kayıt için ayrılmış `COUPON` kodu yazılır ve
 * `unknown: true` döner → sunucu satışa `UNKNOWN_TENDER` uyarısı ekler (Prompt 15'te sessizce COUPON yazılıyordu).
 */
export function normalizePosTenderType(raw) {
    const value = raw.trim().toUpperCase();
    const alias = LEGACY_POS_TENDER_ALIASES[value];
    if (alias)
        return { type: alias, unknown: false };
    const parsed = posTenderTypeSchema.safeParse(value);
    return parsed.success ? { type: parsed.data, unknown: false } : { type: "COUPON", unknown: true };
}
export const posSaleLinePayloadSchema = z.object({
    lineNo: z.number().int().min(1).max(500),
    kind: lineKind.default("ITEM"),
    productId: uuidSchema.nullable().optional(),
    barcode: z.string().trim().max(48).nullable().optional(),
    /** Ürün katalogda yoksa fişte görünen ad (uyarıyla birlikte saklanır). */
    description: z.string().trim().max(200).nullable().optional(),
    quantity: looseQuantity,
    unitPrice: looseUnitPrice,
    gross: looseMoney.describe("Satır brüt tutarı (KDV dâhil, indirim ÖNCESİ, kuruş)"),
    vatRateCode: z.string().trim().min(1).max(40).nullable().optional(),
    /** `FUEL` satırında ZORUNLUDUR: `fuel_transactions.agent_tx_id`. */
    fuelAgentTxId: z.string().trim().max(100).nullable().optional(),
    ageRestricted: z.boolean().nullable().optional(),
    /** Prompt 17: abonelik hakkıyla sıfırlanan satır (pos-local-api §7.2 kural 4). */
    subscriptionId: uuidSchema.nullable().optional().catch(null),
    /** Prompt 19: `kind: EV` satırında ödenen şarj oturumu (pos-local-api §9 kural 1). */
    evSessionId: uuidSchema.nullable().optional().catch(null),
});
export const posSaleDiscountPayloadSchema = z.object({
    lineNo: z.number().int().min(1).max(500),
    campaignId: uuidSchema.nullable().optional(),
    amount: looseNonNegativeMoney,
    reason: z.string().trim().max(60).nullable().optional(),
});
/**
 * Satış ANINDA verilen limit kararı. **Gövdeyi AJAN yazar** (POS'un gönderdiği yok sayılır): karar ajanın
 * önbelleğindeki limit + `posSettings.offlineCreditTolerance` ile verilir. Sunucu bunu bilgi olarak saklar ve
 * AYRICA gerçek limiti denetler (aşımsa alarm + cari notu; satış geri ALINMAZ).
 *
 * `.loose()`: ajan sürümü yeni alan eklerse kayıt düşmesin (protokol §8 geriye uyumlu genişletme).
 */
export const posCreditCheckPayloadSchema = z
    .object({
    verdict: z.enum(["ALLOWED", "OVERRIDDEN"]).catch("ALLOWED").default("ALLOWED"),
    /** `CACHE` = ajanın önbelleğindeki limitten, `NONE` = cari önbellekte yoktu (karar verilemedi). */
    source: z.enum(["CACHE", "NONE"]).catch("CACHE").default("CACHE"),
    availableBefore: moneySchema.nullable().optional(),
    availableAfter: moneySchema.nullable().optional(),
    /** Toleranstan kullanılan tutar (kuruş); 0 = tolerans kullanılmadı. */
    toleranceUsed: moneySchema.nullable().optional(),
    overridden: z.boolean().nullable().optional().describe("Müdür PIN'iyle geçersiz kılındı mı"),
})
    .loose();
export const posSaleTenderPayloadSchema = z
    .object({
    /** Ham tür (en çok 40 karakter); `normalizePosTenderType` ile normalleştirilir — bilinmeyen tür satışı düşürmez. */
    type: z.string().trim().min(1).max(40).catch("UNKNOWN"),
    amount: looseMoney,
    changeGiven: looseNonNegativeMoney.nullable().optional(),
    posTerminalId: uuidSchema.nullable().optional(),
    slipNo: z.string().trim().max(40).nullable().optional(),
    counterpartyId: uuidSchema.nullable().optional(),
    creditCheck: posCreditCheckPayloadSchema.nullable().optional(),
    /** Prompt 17: `LOYALTY_POINTS` ödemesinde harcanan puan (tam sayı). */
    points: z
        .union([z.string().regex(/^\d{1,15}$/), z.number().int().nonnegative()])
        .transform((value) => String(value))
        .nullable()
        .optional()
        .catch(null),
})
    .transform(({ type, ...rest }) => {
    const normalized = normalizePosTenderType(type);
    return { ...rest, type: normalized.type, unknownType: normalized.unknown ? type : null };
});
/** KVKK: plaka / telefon kişisel veridir; olay yükünde taşınır ama OLAY ŞEMASINA (outbox) girmez. */
export const posSaleCustomerPayloadSchema = z.object({
    counterpartyId: uuidSchema.nullable().optional(),
    vehiclePlate: z.string().trim().max(20).nullable().optional(),
    phone: z.string().trim().max(30).nullable().optional(),
    loyaltyCard: z.string().trim().max(60).nullable().optional(),
    // ── Prompt 17 (pos-local-api §7.2) — bozuk değer satışı düşürmez, `null` sayılır ──
    customerProfileId: uuidSchema.nullable().optional().catch(null),
    vehicleId: uuidSchema.nullable().optional().catch(null),
    fleetDriverId: uuidSchema.nullable().optional().catch(null),
    odometerKm: z
        .union([z.number().int().nonnegative().max(9_999_999), z.string().regex(/^\d{1,7}$/)])
        .transform((value) => Number(value))
        .nullable()
        .optional()
        .catch(null),
    /** Kampanya hesabında kullanılan seviye (denetim izi). */
    loyaltyTier: z.string().trim().max(40).nullable().optional().catch(null),
    identifiedBy: z
        .enum(["CARD", "QR", "PLATE", "TAG", "DRIVER_CARD", "PHONE", "MANUAL"])
        .nullable()
        .optional()
        .catch(null),
});
/** Prompt 17: satışta okutulan kupon (indirim satırı `campaignId` ile bağlıdır). */
export const posSaleCouponPayloadSchema = z.object({
    couponId: uuidSchema.nullable().optional().catch(null),
    code: z.string().trim().min(1).max(40),
    campaignId: uuidSchema.nullable().optional().catch(null),
});
/**
 * Prompt 17: ajanın satış anında verdiği filo kural kararı (pos-local-api §7.2 kural 1). Gövdeyi AJAN yazar; sunucu
 * saklar ve gerçek kullanım defteriyle YENİDEN değerlendirir. `.loose()`: ajan yeni alan eklerse kayıt düşmez.
 */
export const posFleetAuthorizationPayloadSchema = z
    .object({
    verdict: z.enum(["ALLOWED", "OVERRIDDEN"]).catch("ALLOWED").default("ALLOWED"),
    source: z.enum(["CACHE", "NONE"]).catch("CACHE").default("CACHE"),
    action: z.enum(["NONE", "BLOCK", "APPROVAL"]).catch("NONE").default("NONE"),
    violations: z
        .array(z
        .object({
        code: z.string().trim().max(40),
        message: z.string().trim().max(300).nullable().optional(),
        meta: z.record(z.string(), z.unknown()).nullable().optional(),
    })
        .loose())
        .max(20)
        .default([])
        .catch([]),
    warnings: z.array(z.string().trim().max(40)).max(10).default([]).catch([]),
})
    .loose();
export const posSaleInvoiceRequestPayloadSchema = z.object({
    buyerTaxId: z.string().trim().min(10).max(11),
    buyerName: z.string().trim().min(1).max(300),
    buyerTaxOffice: z.string().trim().max(100).nullable().optional(),
    buyerAddress: z.string().trim().max(500).nullable().optional(),
    buyerEmail: z.string().trim().max(254).nullable().optional(),
});
export const posSaleReceiptPayloadSchema = z.object({
    fiscalDeviceSerial: z.string().trim().min(1).max(40),
    zNo: z.number().int().min(1).max(1_000_000),
    receiptNo: z.number().int().min(1).max(100_000_000),
    receiptRef: z.string().trim().max(64).nullable().optional(),
    printedAt: timestampSchema.nullable().optional(),
});
export const posSaleVatLinePayloadSchema = z.object({
    rate: rateSchema,
    base: looseMoney,
    vat: looseMoney,
});
export const posSaleTotalsPayloadSchema = z.object({
    gross: looseMoney,
    discount: looseMoney.nullable().optional(),
    net: looseMoney,
    vat: z.array(posSaleVatLinePayloadSchema).max(20).nullable().optional(),
});
/** `PosSale` — `occurredAt` olayın `deviceTime`'ıdır (protokol §3.1), yükte TAŞINMAZ. */
export const agentPosSalePayloadSchema = z.object({
    agentSaleId: z.string().trim().min(1).max(100),
    staffId: uuidSchema.nullable().optional(),
    approvedByStaffId: uuidSchema.nullable().optional(),
    cashRegisterId: uuidSchema.nullable().optional(),
    lines: z.array(posSaleLinePayloadSchema).min(1).max(500),
    discounts: z.array(posSaleDiscountPayloadSchema).max(500).default([]),
    tenders: z.array(posSaleTenderPayloadSchema).max(20).default([]),
    customer: posSaleCustomerPayloadSchema.nullable().optional(),
    invoiceRequest: posSaleInvoiceRequestPayloadSchema.nullable().optional(),
    ageConfirmation: z
        .object({
        confirmedByStaffId: uuidSchema.nullable().optional(),
        at: timestampSchema.nullable().optional(),
    })
        .nullable()
        .optional(),
    receipt: posSaleReceiptPayloadSchema.nullable().optional(),
    totals: posSaleTotalsPayloadSchema.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    // ── Prompt 17 ──
    coupons: z.array(posSaleCouponPayloadSchema).max(20).default([]).catch([]),
    fleetAuthorization: posFleetAuthorizationPayloadSchema.nullable().optional().catch(null),
    // ── Prompt 18 ── el yıkama iş emri ödemesi (ajan doğrulamaz; sunucu bağlar, tutar farkını uyarır).
    washWorkOrderNo: z
        .string()
        .regex(/^[A-Z]{3}\d{10}$/)
        .nullable()
        .optional()
        .catch(null),
});
/**
 * `PosSaleVoid` — TAM iptal (`kind: "VOID"`, `lines` yok) ya da KISMİ iade (`kind: "RETURN"`, `lines` =
 * iade edilen satır numaraları). `voidSaleId` ajanın ürettiği YENİ kayıt kimliğidir; sunucuda `RETURN`
 * satışının `agent_sale_id`'si olur. İade fişinin referansı ajanda `<orijinal>/I<fişNo>` biçiminde üretilir.
 *
 * `kind` verilmezse `lines`'tan türetilir (satır listesi varsa kısmi iade). `totals` ajanın hesabıdır ve
 * NEGATİFTİR; sunucu kendi hesabını yapar, tutmazsa uyarı üretir (satışı düşürmez).
 *
 * `tenders` müşteriye GERİ ÖDENEN tutarlardır (NEGATİF; nakitte para üstü zaten düşülmüş). Ajan bunları sırayla
 * dağıtır (önce ilk ödeme türü); verilmezse sunucu tam iptalde yürürlükteki ödemeleri ters yazar, kısmi iadede
 * ödeme dağılımını BİLEMEZ (eski ajan). Daha önce iade edilmiş satır sunucuda yeniden ters YAZILMAZ.
 */
export const agentPosSaleVoidPayloadSchema = z.object({
    agentSaleId: z.string().trim().min(1).max(100).describe("İptal edilen satış"),
    voidSaleId: z.string().trim().max(100).nullable().optional().describe("Yeni RETURN kaydının kimliği"),
    kind: z.enum(["RETURN", "VOID"]).nullable().optional(),
    reason: z.string().trim().min(3).max(500),
    approvedByStaffId: uuidSchema.nullable().optional(),
    lines: z.array(z.number().int().min(1).max(500)).max(500).nullable().optional(),
    receipt: posSaleReceiptPayloadSchema.nullable().optional(),
    totals: posSaleTotalsPayloadSchema.nullable().optional(),
    tenders: z
        .array(posSaleTenderPayloadSchema)
        .max(20)
        .nullable()
        .optional()
        .describe("Geri ödenen tutarlar (negatif, para üstü düşülmüş)"),
});
/**
 * `PosSaleRetender` — ödeme türü düzeltmesi (pos-local-api §4): akaryakıt satırı İADE EDİLEMEZ (litre
 * pompadan çıktı), yalnızca ödemesi düzeltilir. Müdür onayı zorunludur.
 *
 * Sunucuda: eski tahsilat satırları GEÇERSİZ KILINIR (silinmez), yenileri yazılır; akaryakıt satırı varsa
 * `fuel_transactions`'ın ödeme türü / carisi yeni ödemeye göre güncellenir; veresiye eklendiyse vardiya fişi
 * üretilir, kalktıysa iptal edilir.
 */
export const agentPosSaleRetenderPayloadSchema = z.object({
    agentSaleId: z.string().trim().min(1).max(100),
    reason: z.string().trim().min(3).max(500),
    approvedByStaffId: uuidSchema.nullable().optional(),
    /** Ajanın elindeki ESKİ ödemeler (bilgi / denetim); sunucu kendi kayıtlarını esas alır. */
    previousTenders: z.array(posSaleTenderPayloadSchema).max(20).default([]),
    tenders: z.array(posSaleTenderPayloadSchema).min(1).max(20),
});
// ── Ana veri genişletmesi (protokol §2, Prompt 15) ───────────────────────────
/**
 * Ajanın POS için ihtiyaç duyduğu ana veri. Alan adları `docs/domain/agent-protocol.md` §2 genişletmesiyle
 * BİREBİRDİR (Go ajanı ve POS arayüzü buna göre yazılır).
 */
export const agentMasterPosProductSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    unit: z.string(),
    productType: z.string(),
    businessUnit: z.string(),
    accountingGroup: z.string(),
    vatRateCode: z.string(),
    vatRatePercent: rateSchema.nullable().describe("Bugün geçerli KDV yüzdesi; kod çözülemiyorsa null"),
    /** Market / hizmet ürünü için KURUŞ birim fiyat; fiyat yoksa null. */
    unitPrice: nonNegativeMoneySchema.nullable(),
    /** Akaryakıt / LPG için ₺/L (4 ondalık); diğer ürünlerde null. */
    unitPricePerLitre: unitPriceSchema.nullable(),
    barcodes: z.array(z.string()),
    isAgeRestricted: z.boolean(),
    quickKeySlot: z.number().int().nullable(),
    categoryId: uuidSchema.nullable(),
});
/** KVKK: personel ADI kasa ekranı için gereklidir; başka kişisel veri (TCKN, telefon, adres) YOKTUR. */
export const agentMasterStaffSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string().describe("KVKK: kişisel veri — kasa ekranında gösterilir"),
    duty: z.string(),
    isSupervisor: z
        .boolean()
        .describe("Görev SUPERVISOR ya da kullanıcı hesabı `shifts.close.manage` iznine sahip"),
    pinHash: z.string().nullable().describe("argon2id özeti; PIN'i olmayan personel POS'a giremez"),
});
export const agentMasterPosTerminalSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
});
export const agentMasterOpenShiftSchema = z.object({
    shiftId: uuidSchema,
    shiftNo: z.string(),
    businessDate: localDateSchema,
    openedAt: timestampSchema,
    members: z.array(z.object({
        staffId: uuidSchema,
        duty: z.string(),
        cashRegisterId: uuidSchema.nullable(),
    })),
});
export const agentMasterPosSettingsSchema = z.object({
    fiscalRequired: z.boolean(),
    offlineCreditToleranceKurus: nonNegativeMoneySchema,
    pendingFuelAutoSettleMinutes: z.number().int().nonnegative(),
    ageRestrictedConfirmation: z.boolean(),
    receiptFooter: z.string().nullable(),
});
//# sourceMappingURL=protocol.js.map